import type {
  EmployeeQueryParams,
  EmployeesResponse,
  EmployeeStats,
  EmployeeStatus,
  EmployeeDetail,
  EmergencyContact,
} from '@/types/employee';

export const ACTIVE_EMPLOYEE_STATUSES: EmployeeStatus[] = ['active', 'probation', 'onboarding'];
import type {
  EmployeeNote,
  CreateEmployeeNoteInput,
  UpdateEmployeeNoteInput,
  EmployeeNotesResponse,
  EmployeeNoteResponse,
  Tag,
  TagsResponse,
} from '@/types/employee-note';
import type { EmployeeAuditField, EmployeeAuditPage } from '@/types/employee-audit';
import api from './api';

export async function getEmployees(params: EmployeeQueryParams = {}): Promise<EmployeesResponse> {
  const { data } = await api.get<EmployeesResponse>('/employees', { params });
  return data;
}

export async function getEmployee(id: string): Promise<EmployeeDetail> {
  const { data } = await api.get<EmployeeDetail>(`/employees/${id}`);
  return data;
}

export async function getEmployeeStats(
  params?: Omit<EmployeeQueryParams, 'page' | 'limit'>
): Promise<EmployeeStats> {
  const { data } = await api.get<EmployeeStats>('/employees/stats', { params });
  return data;
}

export async function deactivateEmployee(
  id: string,
  terminationDate?: string,
  /**
   * Where each of their direct reports goes. Required by the API whenever the
   * employee still has any — without it the request comes back 409
   * `HAS_DIRECT_REPORTS` listing the people left unsettled (HW-803).
   */
  reportReassignments?: Array<{ employeeId: string; managerId: string | null }>
): Promise<void> {
  await api.put(`/employees/${id}`, {
    status: 'terminated',
    terminationDate: terminationDate || new Date().toISOString().split('T')[0],
    ...(reportReassignments ? { reportReassignments } : {}),
  });
}

export async function reactivateEmployee(id: string): Promise<void> {
  await api.put(`/employees/${id}`, {
    status: 'active',
  });
}

export interface UpdatePersonalInfoInput {
  dateOfBirth?: string | null;
  phone?: string | null;
  personalEmail?: string | null;
  address?: string | null;
  corporateCvUrl?: string | null;
}

export interface UpdateEmployeeInput {
  firstName?: string;
  lastName?: string;
  preferredName?: string | null;
  workEmail?: string;
  avatarUrl?: string | null;
  positionId?: string | null;
  departmentId?: string | null;
  divisionId?: string | null;
  managerId?: string | null;
  managerIds?: string[];
  seniorityLevelId?: string | null;
  employmentTypeId?: string;
  /** @deprecated Use employmentTypeId instead */
  employmentType?: 'full_time' | 'part_time' | 'contract' | 'intern';
  startDate?: string;
  probationEndDate?: string | null;
  nextReviewDate?: string | null;
  status?: 'active' | 'probation' | 'onboarding' | 'terminated';
  personalInfo?: UpdatePersonalInfoInput;
}

export async function updateEmployee(
  id: string,
  input: UpdateEmployeeInput
): Promise<EmployeeDetail> {
  const { data } = await api.put<{ success: boolean; data: EmployeeDetail }>(
    `/employees/${id}`,
    input
  );
  return data.data;
}

export async function completeProbation(id: string): Promise<EmployeeDetail> {
  return updateEmployee(id, { status: 'active' });
}

export async function extendProbation(id: string, newEndDate: string): Promise<EmployeeDetail> {
  return updateEmployee(id, { probationEndDate: newEndDate });
}

export async function setProbation(id: string, endDate: string): Promise<EmployeeDetail> {
  return updateEmployee(id, { status: 'probation', probationEndDate: endDate });
}

// Create Employee API

export interface CreateEmployeeInput {
  firstName: string;
  lastName: string;
  preferredName?: string;
  workEmail: string;
  personalEmail?: string;
  positionId?: string;
  departmentId?: string;
  divisionId?: string;
  managerId?: string;
  managerIds?: string[];
  seniorityLevelId?: string;
  /** Employment type ID from /employment-types (preferred) */
  employmentTypeId?: string;
  /** @deprecated Use employmentTypeId instead */
  employmentType?: 'full_time' | 'part_time' | 'contract' | 'intern';
  startDate: string;
  probationEndDate?: string;
  roleId?: string;
}

export async function createEmployee(input: CreateEmployeeInput): Promise<EmployeeDetail> {
  const { data } = await api.post<EmployeeDetail>('/employees', input);
  return data;
}

// Personal Contacts API (private messenger handles on PersonalInfo)

export interface UpdatePersonalContactsInput {
  telegram?: string | null;
  whatsapp?: string | null;
  viber?: string | null;
  signal?: string | null;
  discord?: string | null;
  linkedinUrl?: string | null;
}

export async function updatePersonalContacts(
  employeeId: string,
  input: UpdatePersonalContactsInput
): Promise<void> {
  await api.put(`/employees/${employeeId}/personal-contacts`, input);
}

// Emergency Contacts API

export interface CreateEmergencyContactInput {
  name: string;
  relationship: 'spouse' | 'parent' | 'sibling' | 'friend' | 'other';
  phone?: string;
  email?: string;
  telegram?: string;
  whatsapp?: string;
  viber?: string;
  signal?: string;
  discord?: string;
  linkedin?: string;
  isPrimary?: boolean;
}

export type UpdateEmergencyContactInput = Partial<CreateEmergencyContactInput>;

export interface EmergencyContactResponse {
  success: boolean;
  data: EmergencyContact;
  message: string;
}

export async function createEmergencyContact(
  employeeId: string,
  input: CreateEmergencyContactInput
): Promise<EmergencyContact> {
  const { data } = await api.post<EmergencyContactResponse>(
    `/employees/${employeeId}/emergency-contacts`,
    input
  );
  return data.data;
}

export async function updateEmergencyContact(
  employeeId: string,
  contactId: string,
  input: UpdateEmergencyContactInput
): Promise<EmergencyContact> {
  const { data } = await api.put<EmergencyContactResponse>(
    `/employees/${employeeId}/emergency-contacts/${contactId}`,
    input
  );
  return data.data;
}

export async function deleteEmergencyContact(employeeId: string, contactId: string): Promise<void> {
  await api.delete(`/employees/${employeeId}/emergency-contacts/${contactId}`);
}

// Delete Employee API

export async function deleteEmployee(id: string): Promise<void> {
  await api.delete(`/employees/${id}`);
}

// Upload Avatar API

export interface UploadAvatarResponse {
  success: boolean;
  data: {
    avatarUrl: string;
  };
  message: string;
}

export async function uploadAvatar(id: string, file: File): Promise<string> {
  const formData = new FormData();
  formData.append('avatar', file);

  const { data } = await api.post<UploadAvatarResponse>(`/employees/${id}/avatar`, formData);
  return data.data.avatarUrl;
}

export async function deleteAvatar(id: string): Promise<void> {
  await api.delete(`/employees/${id}/avatar`);
}

// Tags API

export async function getTags(): Promise<Tag[]> {
  const { data } = await api.get<TagsResponse>('/employees/tags');
  return data.data;
}

// Employee Notes API

export async function getEmployeeNotes(employeeId: string): Promise<EmployeeNote[]> {
  const { data } = await api.get<EmployeeNotesResponse>(`/employees/${employeeId}/notes`);
  return data.data;
}

export async function createEmployeeNote(
  employeeId: string,
  input: CreateEmployeeNoteInput
): Promise<EmployeeNote> {
  const { data } = await api.post<EmployeeNoteResponse>(`/employees/${employeeId}/notes`, input);
  return data.data;
}

export async function updateEmployeeNote(
  employeeId: string,
  noteId: string,
  input: UpdateEmployeeNoteInput
): Promise<EmployeeNote> {
  const { data } = await api.put<EmployeeNoteResponse>(
    `/employees/${employeeId}/notes/${noteId}`,
    input
  );
  return data.data;
}

export async function deleteEmployeeNote(employeeId: string, noteId: string): Promise<void> {
  await api.delete(`/employees/${employeeId}/notes/${noteId}`);
}

// Compensations

export interface Compensation {
  id: string;
  amount: string;
  currency: string;
  effectiveDate: string;
  endDate: string | null;
  paymentPeriod: string | null;
  comment: string | null;
  createdAt: string;
}

export async function getEmployeeCompensations(employeeId: string): Promise<Compensation[]> {
  const { data } = await api.get<{ success: boolean; data: Compensation[] }>(
    `/employees/${employeeId}/compensations`
  );
  return data.data;
}

export interface CreateCompensationInput {
  amount: number;
  currency?: string;
  effectiveDate: string;
  endDate?: string;
  paymentPeriod?: string;
  comment?: string;
}

export type UpdateCompensationInput = Partial<CreateCompensationInput>;

export async function createCompensation(
  employeeId: string,
  input: CreateCompensationInput
): Promise<Compensation> {
  const { data } = await api.post<{ success: boolean; data: Compensation }>(
    `/employees/${employeeId}/compensations`,
    input
  );
  return data.data;
}

export async function updateCompensation(
  employeeId: string,
  compensationId: string,
  input: UpdateCompensationInput
): Promise<Compensation> {
  const { data } = await api.put<{ success: boolean; data: Compensation }>(
    `/employees/${employeeId}/compensations/${compensationId}`,
    input
  );
  return data.data;
}

export async function deleteCompensation(
  employeeId: string,
  compensationId: string
): Promise<void> {
  await api.delete(`/employees/${employeeId}/compensations/${compensationId}`);
}

// ─────────────────────────────────────────────────────────────────
//  Additional Compensation (bonuses / stipends)
// ─────────────────────────────────────────────────────────────────

/**
 * Bonus / additional compensation entry.
 *
 * - `type === 'reoccurring'` → recurring payment (`frequency` set, `endsOn`
 *   may be null = ongoing).
 * - `type === 'fixed'`       → one-time lump sum (`frequency` and `endsOn`
 *   are null).
 */
export interface AdditionalCompensation {
  id: string;
  type: 'reoccurring' | 'fixed';
  compensationType: string; // 'Bonus' | 'Equity' | 'Stipend' | …
  description: string | null;
  amount: string;
  currency: string;
  startsOn: string; // ISO date
  endsOn: string | null;
  frequency: string | null; // 'monthly' | 'weekly' | 'yearly' | …
  peopleForceId: number | null;
  createdAt: string;
}

export async function getEmployeeAdditionalCompensations(
  employeeId: string
): Promise<AdditionalCompensation[]> {
  const { data } = await api.get<{ success: boolean; data: AdditionalCompensation[] }>(
    `/employees/${employeeId}/additional-compensations`
  );
  return data.data;
}

export interface CreateAdditionalCompensationInput {
  type: 'reoccurring' | 'fixed';
  compensationType?: string;
  description?: string;
  amount: number;
  currency?: string;
  startsOn: string;
  endsOn?: string;
  frequency?: string;
}

export type UpdateAdditionalCompensationInput = Partial<CreateAdditionalCompensationInput>;

export async function createAdditionalCompensation(
  employeeId: string,
  input: CreateAdditionalCompensationInput
): Promise<AdditionalCompensation> {
  const { data } = await api.post<{ success: boolean; data: AdditionalCompensation }>(
    `/employees/${employeeId}/additional-compensations`,
    input
  );
  return data.data;
}

export async function updateAdditionalCompensation(
  employeeId: string,
  id: string,
  input: UpdateAdditionalCompensationInput
): Promise<AdditionalCompensation> {
  const { data } = await api.put<{ success: boolean; data: AdditionalCompensation }>(
    `/employees/${employeeId}/additional-compensations/${id}`,
    input
  );
  return data.data;
}

export async function deleteAdditionalCompensation(
  employeeId: string,
  id: string
): Promise<void> {
  await api.delete(`/employees/${employeeId}/additional-compensations/${id}`);
}

export async function getEmployeeAuditLog(
  id: string,
  params: { limit?: number; cursor?: string; field?: EmployeeAuditField } = {}
): Promise<EmployeeAuditPage> {
  const { data } = await api.get<EmployeeAuditPage>(`/employees/${id}/audit-log`, { params });
  return data;
}
